import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { toast } from "react-toastify";

import API from "../../utils/axios";
import { authSliceActions } from "./authSlice";
import { userApiTypes } from "../../types";
import store from "../store";
import { PayFreqEnum } from "../../types/enum";

const initialState: userApiTypes = {
  changePasswordData: null,
  openChangePasswordPopUpData: false,
  calculationData: null,
  unitsCalculationData: null,
  superannutationData: null,
  historyCreateData: null,
  historyListData: null,
};

//Api request for change password by user after login
export const changePassword = createAsyncThunk(
  "user/changepassword",
  async (args: any, thunkApi) => {
    const { data, navigate } = args;
    try {
      thunkApi.dispatch(authSliceActions.start_loading());
      const res = await API.patch("/user/changepassword", data);
      thunkApi.dispatch(authSliceActions.end_loading());
      res.data?.success
        ? toast.success("Password changed successfully")
        : toast.error(res.data?.message);
      return res.data;
    } catch (err) {
      const error = err as AxiosError;
      error?.response?.status === 403 && navigate("/");
      error?.response?.status === 403 &&
        thunkApi.dispatch(authSliceActions.signout());
      thunkApi.dispatch(authSliceActions.end_loading());
      return thunkApi.rejectWithValue(error);
    }
  }
);

//Api request for calculation when user enter data
export const calculationApi = createAsyncThunk(
  "/user/calculationApi",
  async (args: any, thunkApi) => {
    const { datas, navigate } = args;
    let apiEndPoint = "/user/calculation";
    const apiPayload = {};
    const payLoad = {
      gross: 0,
      net: 0,
      tax: 0,
      isFreeThreashHoldClaimed: false,
      isStudyLoan: false,
      typeId: 1,
    };
    if (datas.financialYear === "2025/26") {
      if (datas.field === "gross") {
        payLoad.gross = Number(datas.data);
      } else if (datas.field === "netAmountPaid") {
        payLoad.net = Number(datas.data);
      } else if (datas.field === "taxWithdeld") {
        payLoad.tax = Number(datas.data);
      }
      payLoad.isFreeThreashHoldClaimed =
        datas.taxFreeThresholdClaimed === "yes" ? true : false;
      payLoad.isStudyLoan = datas.studyOrTrainingLoan === "yes" ? true : false;
      const typeId = PayFreqEnum[datas.payFreq || "weekly"];
      payLoad.typeId = Number(typeId); //1 for weekly, 2 for fortnightly, 3 for monthly
      Object.assign(apiPayload, payLoad);
      apiEndPoint = "/user/calculation2526";
    } else {
      Object.assign(apiPayload, datas);
    }

    try {
      const res = await API.post(apiEndPoint, apiPayload);
      return res.data;
    } catch (err) {
      const error = err as AxiosError;
      error?.response?.status === 403 && navigate("/");
      error?.response?.status === 403 &&
        thunkApi.dispatch(authSliceActions.signout());
      thunkApi.dispatch(authSliceActions.end_loading());
      return thunkApi.rejectWithValue(error);
    }
  }
);

export const unitsCalculationApi = createAsyncThunk(
  "/user/unitsCalculationApi",
  async (args: any, thunkApi) => {
    const { data, navigate } = args;
    try {
      const res = await API.post("/user/unitsCalculation", data);

      return res.data;
    } catch (err) {
      const error = err as AxiosError;
      error?.response?.status === 403 && navigate("/");
      error?.response?.status === 403 &&
        thunkApi.dispatch(authSliceActions.signout());
      return thunkApi.rejectWithValue(error);
    }
  }
);

export const superannuationApi = createAsyncThunk(
  "/user/superannuationApi",
  async (args: any, thunkApi) => {
    const { data, navigate } = args;
    try {
      const res = await API.post("/user/superannutation", data);

      return res.data;
    } catch (err) {
      const error = err as AxiosError;
      error?.response?.status === 403 && navigate("/");
      error?.response?.status === 403 &&
        thunkApi.dispatch(authSliceActions.signout());
      return thunkApi.rejectWithValue(error);
    }
  }
);

export const historyDetailsApi = createAsyncThunk(
  "/user/historyDetailsApi",
  async (args: any, thunkApi) => {
    const { data, navigate } = args;
    try {
      const res = await API.post("/user/addHistory", data);

      return res.data;
    } catch (err) {
      const error = err as AxiosError;
      error?.response?.status === 403 && navigate("/");
      error?.response?.status === 403 &&
        thunkApi.dispatch(authSliceActions.signout());
      return thunkApi.rejectWithValue(error);
    }
  }
);

export const getHistoryListApi = createAsyncThunk(
  "/user/getHistoryListApi",
  async (args: any, thunkApi) => {
    const { navigate, rowsPerPage, page } = args;
    try {
      const res = await API.get(
        `/user/historyDetails?rows=${rowsPerPage}&page=${page}`
      );

      return res.data;
    } catch (err) {
      const error = err as AxiosError;
      error?.response?.status === 403 && navigate("/");
      error?.response?.status === 403 &&
        thunkApi.dispatch(authSliceActions.signout());
      return thunkApi.rejectWithValue(error);
    }
  }
);

//UserSlice
const userSlice = createSlice({
  name: "user",
  initialState: initialState,
  extraReducers: (builder) => {
    builder.addCase(changePassword.fulfilled, (state, action) => {
      state.changePasswordData = action?.payload;
    });

    builder.addCase(changePassword.rejected, (state: any, action) => {
      state.changePasswordData = action?.payload;
    });

    builder.addCase(calculationApi.fulfilled, (state, action) => {
      state.calculationData = action?.payload;
      state.superannutationData = null;
    });

    builder.addCase(calculationApi.rejected, (state, action: any) => {
      state.calculationData = action?.payload;
    });

    builder.addCase(unitsCalculationApi.fulfilled, (state, action) => {
      state.unitsCalculationData = action?.payload;
    });

    builder.addCase(unitsCalculationApi.rejected, (state, action) => {
      state.unitsCalculationData = action?.payload;
    });

    builder.addCase(superannuationApi.fulfilled, (state, action) => {
      state.superannutationData = action?.payload;
    });

    builder.addCase(superannuationApi.rejected, (state, action) => {
      state.superannutationData = action?.payload;
    });

    builder.addCase(historyDetailsApi.fulfilled, (state, action) => {
      state.historyCreateData = action?.payload;
    });

    builder.addCase(historyDetailsApi.rejected, (state, action) => {
      state.historyCreateData = action?.payload;
    });

    builder.addCase(getHistoryListApi.fulfilled, (state, action) => {
      state.historyListData = action?.payload;
    });

    builder.addCase(getHistoryListApi.rejected, (state, action) => {
      state.historyListData = action?.payload;
    });
  },
  reducers: {
    clearData: (state) => {
      state.changePasswordData = null;
    },
    openChangePasswordPopUp: (state) => {
      state.openChangePasswordPopUpData = true;
    },
    closeChangePasswordPopUp: (state) => {
      state.openChangePasswordPopUpData = false;
    },
    clearCalculationData: (state) => {
      state.calculationData = null;
      state.superannutationData = null;
      state.unitsCalculationData = null;
    },
    clearUnitsCalculationData: (state) => {
      state.unitsCalculationData = null;
    },
  },
});

export default userSlice;
export const userSliceActions = userSlice.actions;
